import { useCallback, useEffect, useState } from "react";

import localStorage from "fond/utils/localStorage";

import { DEFAULT_LEFT_PANEL_WIDTH, DEFAULT_SIDEBAR_WIDTH, DEFAULT_TOP_NAVIGATION_HEIGHT } from "./constants";

const STORAGE_KEY = "DS_PAGE_LAYOUT_UI_STATE";

interface GridState {
  leftSidebarWidth: number;
  leftSidebarFlyoutWidth: number;
  topNavigationHeight: number;
  leftPanelWidth: number;
}

interface PageLayoutUIState {
  gridState: GridState;
  isLeftSidebarCollapsed: boolean;
}

const DEFAULT_STATE: PageLayoutUIState = {
  gridState: {
    leftSidebarWidth: DEFAULT_SIDEBAR_WIDTH,
    leftSidebarFlyoutWidth: DEFAULT_SIDEBAR_WIDTH,
    topNavigationHeight: DEFAULT_TOP_NAVIGATION_HEIGHT,
    leftPanelWidth: DEFAULT_LEFT_PANEL_WIDTH,
  },
  isLeftSidebarCollapsed: false,
};

/**
 * Reads the page layout state from localStorage.
 * Returns the stored state or default state if not found.
 */
const readLayoutState = (): PageLayoutUIState => {
  const stored = localStorage.getItem(STORAGE_KEY, DEFAULT_STATE) as unknown;
  const storedObj = stored && typeof stored === "object" ? (stored as Partial<PageLayoutUIState>) : undefined;

  // Merge with defaults to handle missing keys / older schemas
  return {
    gridState: {
      ...DEFAULT_STATE.gridState,
      ...(storedObj?.gridState && typeof storedObj.gridState === "object" ? storedObj.gridState : undefined),
    },
    isLeftSidebarCollapsed: storedObj?.isLeftSidebarCollapsed ?? DEFAULT_STATE.isLeftSidebarCollapsed,
  };
};

/**
 * Writes the page layout state to localStorage.
 */
const writeLayoutState = (state: PageLayoutUIState): void => {
  localStorage.setItem(STORAGE_KEY, state);
};

/**
 * Hook to manage page layout state from localStorage.
 * Provides getters and setters for individual dimensions.
 */
export const usePageLayoutState = () => {
  const [state, setState] = useState<PageLayoutUIState>(readLayoutState);

  // Persist state changes to localStorage
  useEffect(() => {
    writeLayoutState(state);
  }, [state]);

  const setLeftSidebarWidth = useCallback((width: number) => {
    setState((prev) => ({
      ...prev,
      gridState: {
        ...prev.gridState,
        leftSidebarWidth: width,
      },
    }));
  }, []);

  const setLeftPanelWidth = useCallback((width: number) => {
    setState((prev) => ({
      ...prev,
      gridState: {
        ...prev.gridState,
        leftPanelWidth: width,
      },
    }));
  }, []);

  const setTopNavigationHeight = useCallback((height: number) => {
    setState((prev) => ({
      ...prev,
      gridState: {
        ...prev.gridState,
        topNavigationHeight: height,
      },
    }));
  }, []);

  const setIsLeftSidebarCollapsed = useCallback((collapsed: boolean) => {
    setState((prev) => ({
      ...prev,
      isLeftSidebarCollapsed: collapsed,
    }));
  }, []);

  return {
    gridState: state.gridState,
    isLeftSidebarCollapsed: state.isLeftSidebarCollapsed,
    setLeftSidebarWidth,
    setLeftPanelWidth,
    setTopNavigationHeight,
    setIsLeftSidebarCollapsed,
  };
};

/**
 * Gets the initial dimensions from localStorage without using React state.
 * Useful for initial render to avoid layout shift.
 */
export const getInitialLayoutState = (): PageLayoutUIState => {
  return readLayoutState();
};
